<template>
  <div class="deliverable-container">
    <div v-if="userId && fileRef" class="deliverable-wrapper">
      <div class="deliverable-info-wrapper">
        <FileIcon color="primary" filled />
        <div class="deliverable-info">
          <p class="deliverable-info__name">{{ fileRef?.name }}</p>
          <p class="deliverable-info__sub-info">
            {{ t('session.deliverable.sentAt') }} {{ getDate(fileRef?.updatedAt) }}
          </p>
        </div>
      </div>
      <div class="deliverable-dl-button">
        <CustomButton
          usage="button"
          color="secondary"
          :text="isDownloading ? t('session.deliverable.downloading') : t('ui.button.download')"
          :isLoading="isDownloading"
          :isDisabled="isDownloading"
          data-test-id="download-deliverable"
          @emit:click="handleDownload"
        >
          <template #icon>
            <DownloadIcon color="white" />
          </template>
        </CustomButton>
      </div>
    </div>
    <div v-else class="deliverable-empty">
      <p>{{ t('session.deliverable.noDeliverable') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'

import type { FileType } from '@/features/files/types'
import { downloadFile } from '@/features/files/utils'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import DownloadIcon from '@/features/ui/components/icons/download-icon.vue'
import FileIcon from '@/features/ui/components/icons/file-icon.vue'
import { useI18n } from '@/lib/i18n'
import { getDate } from '@/utils/dates'

const props = defineProps<{
  userId: string | null
  sessionId: string | null
  file: FileType
}>()

const fileRef = computed(() => props.file)
const isDownloading = ref(false)
const { t } = useI18n()

async function handleDownload() {
  if (isDownloading.value || !props.userId || !fileRef.value) return

  isDownloading.value = true

  try {
    await downloadFile({
      file: fileRef.value,
      userId: props.userId,
      toast,
      t,
    })
  } catch (error) {
    // Error is already handled in downloadFile with toast
    console.error('Download failed:', error)
  } finally {
    // Keep loading state for a bit to show download completed
    setTimeout(() => {
      isDownloading.value = false
    }, 1000)
  }
}
</script>

<style lang="scss">
@import './deliverable.scss';
</style>
